import { ApiError, type Api } from "../../../data/api";
import { buildKitView, type KitAudience, type KitChannel, type KitView } from "../../oeffentlich/kit-model";
import { parseAmountToCents } from "@talentkontor/domain";
import { t } from "../../../lib/sprache";

/**
 * Media-Kit und Pitch-Liste im Agentur-Bereich (0074): Profil je Creator (Kurzbeschreibung, Themen, Kennzahlen von Hand mit Stand,
 * Preise), Referenzen aus vergangenen Kooperationen, Vorschlag des Creators, Links an Marken mit Protokoll. Rechte prüft die
 * Datenbank (RLS, media_kit_*-Funktionen); Links entstehen nur über media_kit_create, der Schlüssel steht nur einmal im Ergebnis.
 */

export interface KitProfileRow {
  talent_id: string;
  org_id: string;
  bio_de: string | null;
  bio_en: string | null;
  topics: string[];
  /** Themen auf Englisch für englische Links (0081); leer = die deutschen gelten */
  topics_en: string[];
  channels: KitChannel[];
  rates: KitRate[];
  /** Rollenzeile, Zielgruppe von Hand (0214) */
  roles_de?: string | null;
  roles_en?: string | null;
  audience?: KitAudience | null;
  proposed_bio_de: string | null;
  proposed_bio_en: string | null;
  proposed_topics: string[] | null;
  proposed_at: string | null;
  decided_at: string | null;
  decision: "accepted" | "rejected" | null;
  decision_note: string | null;
  updated_at: string;
}
/** Preis im Media-Kit in Cent (Betragsparser der Fachlogik); leer, negativ oder unlesbar = null. */
export function kitBetragCents(v: string): number | null {
  if (!v.trim()) return null;
  try {
    const c = parseAmountToCents(v);
    return Number.isInteger(c) && c >= 0 ? c : null;
  } catch {
    return null;
  }
}

/** Preis im Kit; label_en (0081) erscheint in englischen Links, leer = die deutsche Bezeichnung */
export interface KitRate { label: string; label_en?: string | null; amount_cents: number; currency: "EUR" | "CHF" }
export interface KitReferenceRow { id: string; talent_id: string; deal_id: string; note: string | null; position: number }
/** Als Referenz geeignet: veröffentlicht bzw. abgeschlossen (gleich app.kit_reference_status_ok) */
export const REFERENZ_STATUS: ReadonlySet<string> = new Set(["published", "reporting", "invoicing", "done"]);
export function referenzTauglich(status: string): boolean {
  return REFERENZ_STATUS.has(status);
}

export interface KitDealRow { id: string; title: string; brand_id: string; status: string; start_month: string | null; platforms: string[] | null; archived_at: string | null; created_at: string }
export interface KitRow {
  id: string;
  kind: "single" | "pitch";
  title: string | null;
  brand_id: string | null;
  recipient_email: string;
  language: "de" | "en";
  show_rates: boolean;
  show_references: boolean;
  show_instagram: boolean;
  message: string | null;
  expires_at: string;
  revoked_at: string | null;
  failed_attempts: number;
  locked_at: string | null;
  sent_at: string | null;
  sent_count: number;
  created_at: string;
}
/**
 * Englische Felder, die im Profil fehlen, obwohl es die deutsche Fassung gibt (SICHT-AGENTUR-REST-16): Ein englischer Link
 * zeigt dort die deutsche Fassung. Die Agentur sieht das schon beim Anlegen des Links, nicht erst in der Vorschau.
 */
export function fehlendeEnglischeFelder(p: Pick<KitProfileRow, "bio_de" | "bio_en" | "topics" | "topics_en" | "rates"> | null | undefined): string[] {
  if (!p) return [];
  const out: string[] = [];
  if (p.bio_de?.trim() && !p.bio_en?.trim()) out.push(t("Kurzbeschreibung", "Short description"));
  if ((p.topics ?? []).length > 0 && (p.topics_en ?? []).length === 0) out.push(t("Themen", "Topics"));
  const ohne = (p.rates ?? []).filter((r) => r.label.trim() && !r.label_en?.trim()).length;
  if (ohne > 0) out.push(ohne === 1 ? t("1 Leistung", "1 service") : t(`${ohne} Leistungen`, `${ohne} services`));
  return out;
}

export interface KitViewRow { kit_id: string; at: string; ok: boolean }
export interface KitWithMeta extends KitRow { talentIds: string[]; views: KitViewRow[] }

const KIT_COLUMNS = "id, kind, title, brand_id, recipient_email, language, show_rates, show_references, show_instagram, message, expires_at, revoked_at, failed_attempts, locked_at, sent_at, sent_count, created_at";
const parse = <T>(r: T | string): T => (typeof r === "string" ? (JSON.parse(r) as T) : r);

export type KitState = "active" | "expired" | "revoked" | "locked";
export function kitState(k: Pick<KitRow, "revoked_at" | "expires_at" | "locked_at">, now = new Date()): KitState {
  if (k.revoked_at) return "revoked";
  if (new Date(k.expires_at) < now) return "expired";
  if (k.locked_at) return "locked";
  return "active";
}
export const KIT_STATE_LABEL: Record<KitState, string> = {
  get active() { return t("aktiv", "active"); }, get expired() { return t("abgelaufen", "expired"); },
  get revoked() { return t("zurückgezogen", "withdrawn"); }, get locked() { return t("gesperrt", "locked"); },
};

/** Link für die Marke; bei Englisch mit ?l=en, damit schon das E-Mail-Tor in der Sprache des Links steht (wie die Mail). */
export function kitLink(origin: string, token: string, language: "de" | "en" = "de"): string {
  return `${origin.replace(/\/$/, "")}/kit/${token}${language === "en" ? "?l=en" : ""}`;
}

/** Links mit Creatorn und Aufrufen; ohne talentId alle sichtbaren Links der Agentur (Pitch-Listen-Seite). */
export async function loadKits(api: Api, orgId: string, talentId?: string): Promise<KitWithMeta[]> {
  const links = await api.select<{ kit_id: string; talent_id: string; position: number }>("media_kit_talents", {
    columns: "kit_id, talent_id, position", filters: talentId ? [["org_id", "eq", orgId], ["talent_id", "eq", talentId]] : [["org_id", "eq", orgId]],
  });
  const ids = [...new Set(links.map((l) => l.kit_id))];
  if (!ids.length) return [];
  const [kits, all, views] = await Promise.all([
    api.select<KitRow>("media_kits", { columns: KIT_COLUMNS, filters: [["id", "in", ids]], order: [["created_at", "desc"]] }),
    talentId ? api.select<{ kit_id: string; talent_id: string; position: number }>("media_kit_talents", { columns: "kit_id, talent_id, position", filters: [["kit_id", "in", ids]] }) : Promise.resolve(links),
    api.select<KitViewRow>("media_kit_views", { columns: "kit_id, at, ok", filters: [["kit_id", "in", ids]], order: [["at", "desc"]] }),
  ]);
  return kits.map((k) => ({
    ...k,
    talentIds: all.filter((l) => l.kit_id === k.id).sort((a, b) => a.position - b.position).map((l) => l.talent_id),
    views: views.filter((v) => v.kit_id === k.id),
  }));
}

/** Foto der Collage oder Marken-Logo im Media-Kit (0214) */
export interface KitAssetRow { id: string; kind: "photo" | "logo"; path: string; label: string | null; position: number }
export const KIT_FOTOS_MAX = 7;
export const KIT_LOGOS_MAX = 24;

export interface KitEditorData {
  assets: KitAssetRow[];
  profile: KitProfileRow | null;
  references: KitReferenceRow[];
  deals: KitDealRow[];
  brands: Map<string, string>;
  kits: KitWithMeta[];
  currency: string;
}

export async function loadKitEditor(api: Api, orgId: string, talentId: string): Promise<KitEditorData> {
  const [assets, profiles, references, deals, brands, kits, settings] = await Promise.all([
    api.select<KitAssetRow>("talent_kit_assets", { columns: "id, kind, path, label, position", filters: [["talent_id", "eq", talentId]], order: [["position", "asc"], ["created_at", "asc"]] }).catch(() => [] as KitAssetRow[]),
    api.select<KitProfileRow>("talent_kit_profiles", { filters: [["talent_id", "eq", talentId]] }),
    api.select<KitReferenceRow>("talent_kit_references", { columns: "id, talent_id, deal_id, note, position", filters: [["talent_id", "eq", talentId]], order: [["position", "asc"]] }),
    api.select<KitDealRow>("deals", { columns: "id, title, brand_id, status, start_month, platforms, archived_at, created_at", filters: [["org_id", "eq", orgId], ["talent_id", "eq", talentId]], order: [["created_at", "desc"]] }),
    api.select<{ id: string; name: string }>("brands", { columns: "id, name", filters: [["org_id", "eq", orgId]] }),
    loadKits(api, orgId, talentId),
    api.select<{ currency: string }>("org_settings", { columns: "currency", filters: [["org_id", "eq", orgId]] }).catch(() => []),
  ]);
  return { assets, profile: profiles[0] ?? null, references, deals, brands: new Map(brands.map((b) => [b.id, b.name])), kits, currency: settings[0]?.currency ?? "EUR" };
}

export type KitProfilePatch = Pick<KitProfileRow, "bio_de" | "bio_en" | "topics" | "channels" | "rates"> & { topics_en?: string[]; roles_de?: string | null; roles_en?: string | null; audience?: KitAudience | null };

// ── Bilder im Media-Kit (0214) ───────────────────────────────────────────────────

function bildElement(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); res(img); };
    img.onerror = () => { URL.revokeObjectURL(url); rej(new Error("bild")); };
    img.src = url;
  });
}
/**
 * Bild vor dem Hochladen verkleinern und neu schreiben: Fotos als JPEG (längste Seite 1800 Pixel), Logos als PNG mit
 * Transparenz (längste Seite 900 Pixel). Dabei fallen Aufnahmeort und Gerätedaten aus der Datei; SVG wird zu PNG.
 */
/**
 * Logo einheitlich dunkel auf durchsichtigem Grund (wie im Vorbild, Praxistest K-053): Ein Logo mit Transparenz behält seine
 * Form; ist es überwiegend hell (weißes Logo), wird das Helle zur Farbe. Ein Logo auf einfarbigem Grund verliert den Grund
 * (Farbe der Ecken). Danach wird der leere Rand abgeschnitten, damit alle Logos gleich groß wirken.
 */
function logoEinfarbig(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const d = ctx.getImageData(0, 0, w, h);
  const p = d.data, n = w * h;
  let durch = 0, lumSum = 0, lumN = 0;
  const lum = (i: number) => (0.2126 * p[i]! + 0.7152 * p[i + 1]! + 0.0722 * p[i + 2]!) / 255;
  for (let i = 0; i < n * 4; i += 4) {
    const a = p[i + 3]!;
    if (a < 250) durch++;
    if (a > 40) { lumSum += lum(i) * a; lumN += a; }
  }
  const klemme = (v: number) => Math.min(1, Math.max(0, v));
  if (durch > n * 0.03) {
    const hell = lumN > 0 && lumSum / lumN > 0.6;
    for (let i = 0; i < n * 4; i += 4) {
      const kraft = hell ? lum(i) : 1 - lum(i);
      p[i + 3] = Math.round(p[i + 3]! * klemme((kraft - 0.15) / 0.5));
    }
  } else {
    const ecke = [0, (w - 1) * 4, (h - 1) * w * 4, (h * w - 1) * 4];
    const bg = [0, 1, 2].map((k) => ecke.reduce((sum, e) => sum + p[e + k]!, 0) / 4);
    for (let i = 0; i < n * 4; i += 4) {
      const dist = Math.max(Math.abs(p[i]! - bg[0]!), Math.abs(p[i + 1]! - bg[1]!), Math.abs(p[i + 2]! - bg[2]!)) / 255;
      p[i + 3] = Math.round(255 * klemme((dist - 0.08) / 0.3));
    }
  }
  for (let i = 0; i < n * 4; i += 4) { p[i] = 29; p[i + 1] = 29; p[i + 2] = 27; }
  ctx.putImageData(d, 0, 0);
}
/** Leeren (durchsichtigen) Rand abschneiden; gibt die neue Leinwand zurück oder die alte, wenn nichts abzuschneiden ist. */
function randWeg(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  const { width: w, height: h } = canvas;
  const p = ctx.getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (p[(y * w + x) * 4 + 3]! > 12) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  if (x1 < x0 || y1 < y0) return canvas;
  const bw = x1 - x0 + 1, bh = y1 - y0 + 1;
  if (bw >= w - 2 && bh >= h - 2) return canvas;
  const out = document.createElement("canvas");
  out.width = bw;
  out.height = bh;
  out.getContext("2d")?.drawImage(canvas, x0, y0, bw, bh, 0, 0, bw, bh);
  return out;
}

export async function kitBildVorbereiten(file: Blob, kind: "photo" | "logo", einfarbig = true): Promise<Blob> {
  if (!/^image\/(png|jpeg|webp|svg\+xml)$/.test(file.type)) throw new Error(t("Bitte ein Bild als PNG, JPG, WebP oder SVG wählen.", "Please choose an image as PNG, JPG, WebP or SVG."));
  if (file.size <= 0 || file.size > 15_728_640) throw new Error(t("Das Bild ist leer oder größer als 15 MB.", "The image is empty or larger than 15 MB."));
  let img: HTMLImageElement;
  try { img = await bildElement(file); } catch { throw new Error(t("Das Bild ließ sich nicht lesen.", "The image could not be read.")); }
  const bw = img.naturalWidth || 900, bh = img.naturalHeight || 600;
  const max = kind === "photo" ? 1800 : 900;
  const f = Math.min(1, max / Math.max(bw, bh)) * (file.type === "image/svg+xml" && Math.max(bw, bh) < 600 ? 600 / Math.max(bw, bh) : 1);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bw * f));
  canvas.height = Math.max(1, Math.round(bh * f));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error(t("Das Bild ließ sich nicht verarbeiten.", "The image could not be processed."));
  if (kind === "photo") { ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  let fertig = canvas;
  if (kind === "logo") {
    if (einfarbig) logoEinfarbig(ctx, canvas.width, canvas.height);
    fertig = randWeg(canvas);
  }
  const out = await new Promise<Blob | null>((res) => fertig.toBlob(res, kind === "photo" ? "image/jpeg" : "image/png", 0.86));
  if (!out) throw new Error(t("Das Bild ließ sich nicht verarbeiten.", "The image could not be processed."));
  return out;
}

export async function addKitAsset(api: Api, orgId: string, talentId: string, kind: "photo" | "logo", file: Blob, label: string | null, position: number, einfarbig = true): Promise<void> {
  const bild = await kitBildVorbereiten(file, kind, einfarbig);
  const path = `${orgId}/talent/${talentId}/kit-${kind === "photo" ? "foto" : "logo"}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${kind === "photo" ? "jpg" : "png"}`;
  await api.upload("documents", path, bild);
  const rows = await api.insert<KitAssetRow>("talent_kit_assets", { org_id: orgId, talent_id: talentId, kind, path, label: label?.trim().slice(0, 80) || null, position });
  if (!rows[0]) throw new ApiError(t("Das Bild wurde nicht gespeichert. Der Creator ist für dein Konto nicht freigegeben.", "The image was not saved. The creator is not available to your account."), { code: "42501" });
}
export async function updateKitAsset(api: Api, id: string, patch: { label?: string | null; position?: number }): Promise<void> {
  await api.update("talent_kit_assets", [["id", "eq", id]], patch);
}
export async function removeKitAsset(api: Api, id: string): Promise<void> {
  await api.remove("talent_kit_assets", [["id", "eq", id]]);
}

// ── Zielgruppe von Hand (0214) ───────────────────────────────────────────────────

export interface AnteilZeile { key: string; pct: string }
export interface ZielgruppeFormular { asOf: string; f: string; m: string; u: string; alter: AnteilZeile[]; laender: AnteilZeile[] }
const pctText = (v: number | undefined) => (v == null ? "" : String(Math.round(v * 1000) / 10).replace(".", ","));
export function zielgruppeFormular(a: KitAudience | null | undefined, heute: string): ZielgruppeFormular {
  const zeilen = (d: Record<string, number> | undefined) => Object.entries(d ?? {}).sort((x, y) => y[1] - x[1]).map(([key, v]) => ({ key, pct: pctText(v) }));
  return { asOf: a?.as_of ?? heute, f: pctText(a?.gender?.f), m: pctText(a?.gender?.m), u: pctText(a?.gender?.u), alter: zeilen(a?.age), laender: zeilen(a?.country) };
}
/** Formular → gespeicherte Zielgruppe (Anteile 0 bis 1); leer = null. */
export function zielgruppeAusFormular(z: ZielgruppeFormular): { audience: KitAudience | null; fehler: string | null } {
  const pct = (v: string): number | null | undefined => {
    const x = v.trim().replace(/%$/, "").trim().replace(",", ".");
    if (!x) return null;
    const n = Number(x);
    return Number.isFinite(n) && n >= 0 && n <= 100 ? Math.round(n * 10) / 1000 : undefined;
  };
  const gender: Record<string, number> = {};
  for (const [k, v] of [["f", z.f], ["m", z.m], ["u", z.u]] as const) {
    const n = pct(v);
    if (n === undefined) return { audience: null, fehler: t("Zielgruppe: Anteile als Zahl zwischen 0 und 100.", "Audience: shares as a number between 0 and 100.") };
    if (n !== null) gender[k] = n;
  }
  const gruppe = (zeilen: AnteilZeile[], name: string): Record<string, number> | string => {
    const out: Record<string, number> = {};
    for (const r of zeilen) {
      const key = r.key.trim();
      const n = pct(r.pct);
      if (!key && n === null) continue;
      if (!key || key.length > 40) return t(`Zielgruppe, ${name}: Bitte eine Bezeichnung mit höchstens 40 Zeichen.`, `Audience, ${name}: please enter a name with at most 40 characters.`);
      if (n === undefined || n === null) return t(`Zielgruppe, ${name}: Anteil für „${key}“ als Zahl zwischen 0 und 100.`, `Audience, ${name}: share for “${key}” as a number between 0 and 100.`);
      out[key] = n;
    }
    return Object.keys(out).length > 8 ? t(`Zielgruppe, ${name}: höchstens 8 Zeilen.`, `Audience, ${name}: at most 8 rows.`) : out;
  };
  const age = gruppe(z.alter, t("Alter", "age"));
  if (typeof age === "string") return { audience: null, fehler: age };
  const country = gruppe(z.laender, t("Länder", "countries"));
  if (typeof country === "string") return { audience: null, fehler: country };
  if (!Object.keys(gender).length && !Object.keys(age).length && !Object.keys(country).length) return { audience: null, fehler: null };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(z.asOf)) return { audience: null, fehler: t("Zielgruppe: Bitte den Stand (Datum) der Zahlen angeben.", "Audience: please enter the as-of date of the figures.") };
  return { audience: { as_of: z.asOf, ...(Object.keys(gender).length ? { gender } : {}), ...(Object.keys(age).length ? { age } : {}), ...(Object.keys(country).length ? { country } : {}) }, fehler: null };
}

/** Profil anlegen bzw. ändern (RLS: wer den Creator sehen darf). */
export async function saveKitProfile(api: Api, orgId: string, talentId: string, patch: KitProfilePatch, exists: boolean): Promise<KitProfileRow> {
  const rows = exists
    ? await api.update<KitProfileRow>("talent_kit_profiles", [["talent_id", "eq", talentId]], patch)
    : await api.insert<KitProfileRow>("talent_kit_profiles", { talent_id: talentId, org_id: orgId, ...patch });
  if (!rows[0]) throw new ApiError(t("Das Media-Kit wurde nicht gespeichert. Es ist für dein Konto nicht freigegeben.", "The media kit was not saved. It is not available to your account."), { code: "42501" });
  return rows[0];
}

/** Referenz setzen (mit Text) oder entfernen. */
export async function setKitReference(api: Api, orgId: string, talentId: string, dealId: string, on: boolean, note: string | null, existing: KitReferenceRow | undefined, position = 0): Promise<void> {
  if (!on) {
    if (existing) await api.remove("talent_kit_references", [["id", "eq", existing.id]]);
    return;
  }
  const clean = note?.trim() ? note.trim().slice(0, 200) : null;
  if (existing) await api.update("talent_kit_references", [["id", "eq", existing.id]], { note: clean });
  else await api.insert("talent_kit_references", { org_id: orgId, talent_id: talentId, deal_id: dealId, note: clean, position });
}

export async function decideKitProposal(api: Api, talentId: string, accept: boolean, note: string | null): Promise<true> {
  await api.rpc("talent_kit_decide", { p_talent: talentId, p_accept: accept, p_note: note?.trim() || null });
  return true;
}

export interface KitCreateInput {
  talentIds: string[];
  email: string;
  title: string;
  brandId: string | null;
  language: "de" | "en";
  days: number;
  showRates: boolean;
  showReferences: boolean;
  showInstagram: boolean;
  message: string;
}
export async function createKit(api: Api, orgId: string, i: KitCreateInput): Promise<{ kit_id: string; token: string }> {
  return parse(await api.rpc<{ kit_id: string; token: string } | string>("media_kit_create", {
    p_org: orgId, p_talents: i.talentIds, p_email: i.email.trim().toLowerCase(), p_title: i.title.trim() || null, p_brand: i.brandId, p_language: i.language,
    p_days: i.days, p_show_rates: i.showRates, p_show_references: i.showReferences, p_show_instagram: i.showInstagram, p_message: i.message.trim() || null,
  }));
}
export async function revokeKit(api: Api, kitId: string): Promise<true> {
  await api.rpc("media_kit_revoke", { p_kit: kitId });
  return true;
}
/** Versand über den Postausgang; der bisherige Link gilt danach nicht mehr (neuer Schlüssel). */
export async function sendKit(api: Api, kitId: string, message: string | null): Promise<{ token: string; sent_count: number }> {
  return parse(await api.rpc<{ token: string; sent_count: number } | string>("media_kit_send", { p_kit: kitId, p_message: message?.trim() || null }));
}
/** Vorschau so, wie die Marke den Link sieht (liest nur, kein Protokolleintrag). */
export async function previewKit(api: Pick<Api, "read">, kitId: string): Promise<KitView> {
  const v = buildKitView(parse(await api.read<unknown>("media_kit_preview", { p_kit: kitId })));
  if (!v) throw new ApiError(t("Die Vorschau ließ sich nicht laden.", "The preview could not be loaded."));
  return v;
}

/** Eingaben des Link-Formulars prüfen (vor dem Aufruf, damit Fehler am Feld stehen). */
export function checkKitInput(i: KitCreateInput): string | null {
  if (!i.talentIds.length) return t("Bitte mindestens einen Creator wählen.", "Please choose at least one creator.");
  if (i.talentIds.length > 30) return t("Höchstens 30 Creator in einer Liste.", "At most 30 creators in one list.");
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(i.email.trim())) return t("Bitte die E-Mail-Adresse der Marke angeben; sie ist der Schlüssel zum Link.", "Please enter the brand's email address; it is the key to the link.");
  if (!Number.isInteger(i.days) || i.days < 1 || i.days > 90) return t("Gültigkeit 1 bis 90 Tage.", "Validity 1 to 90 days.");
  if (i.title.length > 120) return t("Der Titel darf höchstens 120 Zeichen haben.", "The title may have at most 120 characters.");
  if (i.message.length > 2000) return t("Die Nachricht darf höchstens 2000 Zeichen haben.", "The message may have at most 2000 characters.");
  return null;
}

// ── Formular des Profils ─────────────────────────────────────────────────────────

export interface KanalZeile { platform: string; handle: string; followers: string; avgViews: string; engagement: string; asOf: string; views30?: string; storyViews?: string }
export interface PreisZeile { label: string; labelEn?: string; amount: string; currency: "EUR" | "CHF" }

const zahl = (v: string): number | null | undefined => {
  const t = v.trim().replace(/[.\s’']/g, "");
  if (!t) return null;
  return /^\d{1,10}$/.test(t) ? Number(t) : undefined;
};

export function kanalZeilen(channels: readonly KitChannel[]): KanalZeile[] {
  return channels.map((c) => ({
    platform: c.platform, handle: c.handle ?? "", followers: c.followers == null ? "" : String(c.followers), avgViews: c.avg_views == null ? "" : String(c.avg_views),
    engagement: c.engagement_bp == null ? "" : String(c.engagement_bp / 100).replace(".", ","), asOf: c.as_of ?? "",
    views30: c.views_30d == null ? "" : String(c.views_30d), storyViews: c.story_views == null ? "" : String(c.story_views),
  }));
}
export function preisZeilen(rates: readonly KitRate[]): PreisZeile[] {
  return rates.map((r) => ({ label: r.label, labelEn: r.label_en ?? "", amount: (r.amount_cents / 100).toFixed(2).replace(".", ","), currency: r.currency }));
}

/** Profil-Formular in Datenbankwerte; Zahlen ohne Stand-Datum sind nicht erlaubt (wie die Datenbank). */
export function kitPatchAusFormular(f: { bioDe: string; bioEn: string; themen: string; themenEn?: string; kanaele: KanalZeile[]; preise: PreisZeile[]; rollenDe?: string; rollenEn?: string; zielgruppe?: ZielgruppeFormular }): { patch: KitProfilePatch | null; fehler: string | null } {
  const liste = (text: string): string[] => {
    const out: string[] = [];
    for (const raw of text.split(/[,\n;]/)) {
      const t = raw.trim().replace(/\s+/g, " ");
      if (t && !out.some((x) => x.toLowerCase() === t.toLowerCase())) out.push(t);
    }
    return out;
  };
  const topics = liste(f.themen);
  const topicsEn = liste(f.themenEn ?? "");
  if ([topics, topicsEn].some((l) => l.length > 12 || l.some((t) => t.length < 2 || t.length > 40))) return { patch: null, fehler: t("Höchstens 12 Themen mit je 2 bis 40 Zeichen (je Sprache).", "At most 12 topics with 2 to 40 characters each (per language).") };
  if (f.bioDe.length > 1200 || f.bioEn.length > 1200) return { patch: null, fehler: t("Die Kurzbeschreibung darf höchstens 1200 Zeichen haben.", "The short bio may have at most 1200 characters.") };
  const channels: KitChannel[] = [];
  for (const [i, k] of f.kanaele.entries()) {
    const followers = zahl(k.followers), avg = zahl(k.avgViews), v30 = zahl(k.views30 ?? ""), story = zahl(k.storyViews ?? "");
    if (v30 === undefined || story === undefined) return { patch: null, fehler: t(`Kanal ${i + 1}: Aufrufe als ganze Zahl.`, `Channel ${i + 1}: views as whole numbers.`) };
    const eng = k.engagement.trim() ? Number(k.engagement.trim().replace(",", ".").replace(/%$/, "")) : null;
    if (followers === undefined || avg === undefined) return { patch: null, fehler: t(`Kanal ${i + 1}: Follower und Aufrufe als ganze Zahl.`, `Channel ${i + 1}: followers and views as whole numbers.`) };
    if (eng !== null && (!Number.isFinite(eng) || eng < 0 || eng > 100)) return { patch: null, fehler: t(`Kanal ${i + 1}: Interaktionsrate zwischen 0 und 100 %.`, `Channel ${i + 1}: engagement rate between 0 and 100 %.`) };
    if ((followers != null || avg != null || eng != null || v30 != null || story != null) && !/^\d{4}-\d{2}-\d{2}$/.test(k.asOf)) return { patch: null, fehler: t(`Kanal ${i + 1}: Bitte den Stand (Datum) der Zahlen angeben.`, `Channel ${i + 1}: please enter the as-of date of the figures.`) };
    if (k.handle.length > 80) return { patch: null, fehler: t(`Kanal ${i + 1}: Der Name ist zu lang.`, `Channel ${i + 1}: the name is too long.`) };
    channels.push({
      platform: k.platform, ...(k.handle.trim() ? { handle: k.handle.trim() } : {}), ...(followers != null ? { followers } : {}), ...(avg != null ? { avg_views: avg } : {}),
      ...(eng != null ? { engagement_bp: Math.round(eng * 100) } : {}), ...(k.asOf ? { as_of: k.asOf } : {}),
      ...(v30 != null ? { views_30d: v30 } : {}), ...(story != null ? { story_views: story } : {}),
    });
  }
  if (channels.length > 10) return { patch: null, fehler: t("Höchstens 10 Kanäle.", "At most 10 channels.") };
  const rates: KitRate[] = [];
  for (const [i, p] of f.preise.entries()) {
    // Betragsparser der Fachlogik: „1.500“ und „1'500“ = 1500 (Tausendertrenner), „1.500,00“ und „1500.00“ = 1500
    const amountCents = kitBetragCents(p.amount);
    if (p.label.trim().length < 2 || p.label.trim().length > 80) return { patch: null, fehler: t(`Preis ${i + 1}: Bitte eine Bezeichnung mit 2 bis 80 Zeichen.`, `Price ${i + 1}: please enter a name with 2 to 80 characters.`) };
    if (amountCents === null) return { patch: null, fehler: t(`Preis ${i + 1}: Bitte einen Betrag angeben, z. B. 1.500 oder 1.500,00.`, `Price ${i + 1}: please enter an amount, e.g. 1,500 or 1,500.00.`) };
    const en = (p.labelEn ?? "").trim();
    if (en && (en.length < 2 || en.length > 80)) return { patch: null, fehler: t(`Preis ${i + 1}: Die englische Bezeichnung braucht 2 bis 80 Zeichen oder bleibt leer.`, `Price ${i + 1}: the English name needs 2 to 80 characters or stays empty.`) };
    rates.push({ label: p.label.trim(), ...(en ? { label_en: en } : {}), amount_cents: amountCents, currency: p.currency });
  }
  if (rates.length > 12) return { patch: null, fehler: t("Höchstens 12 Preise.", "At most 12 prices.") };
  const rollen = [f.rollenDe ?? "", f.rollenEn ?? ""].map((r) => r.trim().replace(/\s+/g, " "));
  if (rollen.some((r) => r.length > 120)) return { patch: null, fehler: t("Die Rollenzeile darf höchstens 120 Zeichen haben.", "The role line may have at most 120 characters.") };
  const zg = f.zielgruppe ? zielgruppeAusFormular(f.zielgruppe) : null;
  if (zg?.fehler) return { patch: null, fehler: zg.fehler };
  return { patch: { bio_de: f.bioDe.trim() || null, bio_en: f.bioEn.trim() || null, topics, topics_en: topicsEn, channels, rates,
    ...(f.rollenDe !== undefined || f.rollenEn !== undefined ? { roles_de: rollen[0] || null, roles_en: rollen[1] || null } : {}), ...(zg ? { audience: zg.audience } : {}) }, fehler: null };
}
